<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M7 (7.1).
 * Assign/unassign this assistant to the account's inboxes — the UI that replaces the console-only
 * EvaInbox.create! (gap A1). Lists every account inbox annotated with its binding state: bound to
 * this assistant (with Remove), bound to another Eva assistant or to Captain (disabled, with the
 * reason), or eligible (with Assign). Surfaces the backend 422 message (e.g. Captain conflict).
 * Tailwind-only; Composition API; self-contained so it mounts under vitest.
 */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaInboxesAPI from 'dashboard/api/eva/inboxes';
import { evaErrorMessages } from 'dashboard/api/eva/errors';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const inboxes = ref([]);
const isLoading = ref(false);
const busyId = ref(null);
const errorMessage = ref('');

const load = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await EvaInboxesAPI.list(props.assistantId);
    inboxes.value = data;
  } catch {
    errorMessage.value = t('EVA_INBOXES.LOAD_ERROR');
  } finally {
    isLoading.value = false;
  }
};

// Surface the backend's validation message (e.g. Captain conflict) when present, else a generic line.
const messageFromError = error => {
  const messages = evaErrorMessages(error);
  return messages.length ? messages.join(' ') : t('EVA_INBOXES.SAVE_ERROR');
};

const assign = async inbox => {
  busyId.value = inbox.id;
  errorMessage.value = '';
  try {
    await EvaInboxesAPI.bind(props.assistantId, inbox.id);
    await load();
  } catch (error) {
    errorMessage.value = messageFromError(error);
  } finally {
    busyId.value = null;
  }
};

const unassign = async inbox => {
  busyId.value = inbox.id;
  errorMessage.value = '';
  try {
    await EvaInboxesAPI.unbind(props.assistantId, inbox.id);
    await load();
  } catch (error) {
    errorMessage.value = messageFromError(error);
  } finally {
    busyId.value = null;
  }
};

const lockReason = inbox => {
  if (inbox.bound_to_captain) return t('EVA_INBOXES.LOCKED_CAPTAIN');
  if (inbox.bound_to_other_eva) return t('EVA_INBOXES.LOCKED_OTHER');
  return '';
};

onMounted(load);

defineExpose({ load, assign, unassign });
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0">
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('EVA_INBOXES.TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-11">{{ t('EVA_INBOXES.SUBTITLE') }}</p>
    </div>

    <p v-if="errorMessage" class="text-sm text-n-ruby-11">{{ errorMessage }}</p>
    <p v-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('EVA_INBOXES.LOADING') }}
    </p>
    <p
      v-else-if="!inboxes.length"
      class="px-6 py-6 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
    >
      {{ t('EVA_INBOXES.EMPTY') }}
    </p>

    <ul
      v-else
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container divide-y divide-n-weak"
    >
      <li
        v-for="inbox in inboxes"
        :key="inbox.id"
        class="flex items-center justify-between gap-3 p-4 min-w-0"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <span
            class="flex items-center gap-1.5 text-sm font-medium truncate text-n-slate-12"
          >
            <span
              v-if="inbox.bound_to_this"
              class="rounded-full size-1.5 shrink-0 bg-n-teal-9"
            />
            {{ inbox.name }}
          </span>
          <span
            v-if="lockReason(inbox)"
            class="inline-flex items-center self-start gap-1 px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-black2 text-n-slate-11"
          >
            <span class="i-lucide-lock size-3" />
            {{ lockReason(inbox) }}
          </span>
        </div>
        <Button
          v-if="inbox.bound_to_this"
          ghost
          ruby
          sm
          :label="t('EVA_INBOXES.REMOVE')"
          :is-loading="busyId === inbox.id"
          @click="unassign(inbox)"
        />
        <Button
          v-else
          ghost
          sm
          :label="t('EVA_INBOXES.ASSIGN')"
          :is-loading="busyId === inbox.id"
          :disabled="inbox.bound_to_other_eva || inbox.bound_to_captain"
          @click="assign(inbox)"
        />
      </li>
    </ul>
  </div>
</template>
